import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Dialog} from 'primeng/dialog';
import {SharedModule} from 'primeng/api';
import {SelectButton} from 'primeng/selectbutton';
import {DirectusTranslationPipe} from '@frontend/directus';
import {ProviderScoreCalculationResult} from '../../services/score-calculation';
import {
  SubCriteriaRow,
  ProviderColumn,
} from '../table/provider-results-table/provider-results-table.types';
import {
  AttributeWithScores,
  EvaluationSubCriteriaImportanceScore,
  ImportanceScoreInfo,
} from '@frontend/shared-api';
import {AttributeTransformationService} from '../../services/attribute-transformation.service';
import {SubcriteriaAttributeScoresTableComponent} from './subcriteria-attribute-scores-table.component';
import {SubcriteriaAttributeScoresProviderFilterComponent} from './subcriteria-attribute-scores-provider-filter.component';
import {SubcriteriaAttributeScoreData} from './subcriteria-attribute-scores.types';
import {SubcriteriaAttributeScoresHeatmapComponent} from './subcriteria-attribute-scores-heatmap.component';

@Component({
  selector: 'app-subcriteria-attribute-scores-modal',
  imports: [
    FormsModule,
    TranslocoPipe,
    Dialog,
    SharedModule,
    SelectButton,
    SubcriteriaAttributeScoresTableComponent,
    SubcriteriaAttributeScoresProviderFilterComponent,
    SubcriteriaAttributeScoresHeatmapComponent,
  ],
  templateUrl: './subcriteria-attribute-scores-modal.component.html',
  styleUrls: ['./subcriteria-attribute-scores-modal.component.scss'],
})
export class SubcriteriaAttributeScoresModalComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly directusTranslationPipe = new DirectusTranslationPipe();
  private readonly attributeTransformationService = inject(AttributeTransformationService);

  readonly visible = input.required<boolean>();
  readonly subCriteria = input<SubCriteriaRow | null>(null);
  readonly providerColumns = input<ProviderColumn[]>([]);
  readonly calculationResult = input<ProviderScoreCalculationResult | null>(null);
  readonly isExponentialScale = input<boolean>(false);

  readonly visibleChange = output<boolean>();

  private readonly selectedProviderIds = signal<string[]>([]);
  protected readonly selectedProviderIdsList = computed(() => this.selectedProviderIds());
  protected readonly viewMode = signal<'table' | 'chart'>('table');

  protected readonly viewOptions = [
    {label: 'Table', value: 'table', translationKey: 'project.steps.viewTable'},
    {label: 'Heatmap', value: 'chart', translationKey: 'project.steps.viewHeatmap'},
  ];

  protected readonly filteredProviders = computed(() => {
    const selected = new Set(this.selectedProviderIds());
    return this.providerColumns().filter((provider) => selected.has(provider.hrToolDirectusId));
  });

  protected readonly subcriteriaLabel = computed(() => this.subCriteria()?.translatedLabel);

  private readonly fulfillmentTypeById = computed(() => {
    const types = this.calculationResult()?.fulfillmentTypes ?? [];
    return new Map(types.map((type) => [type.id, type.type]));
  });

  protected readonly attributeRows = computed<SubcriteriaAttributeScoreData[]>(() => {
    const subCriteria = this.subCriteria();
    const calculationResult = this.calculationResult();
    if (!subCriteria || !calculationResult) return [];

    const directusId = subCriteria.directusId;
    const directusAttributes = calculationResult.attributesMap[directusId] ?? [];
    const subCriteriaScores = this.findSubcriteriaScores(
      calculationResult.requestedCriteria,
      subCriteria,
    );
    const scoresByAttribute = this.mapScoresByAttribute(subCriteriaScores);
    const lang = this.translocoService.getActiveLang();
    const fulfillmentTypeById = this.fulfillmentTypeById();

    const rows: SubcriteriaAttributeScoreData[] = [];
    directusAttributes.forEach((attr) => {
      const attributeScores = scoresByAttribute.get(attr.id);
      if (!attributeScores) {
        return;
      }
      const customerAverageRating = this.calculateCustomerAverage(
        attributeScores.importanceScores ?? [],
        calculationResult.importanceScoreWeights,
      );
      const providerValuesByHrToolId = new Map<string, number | null>();
      this.providerColumns().forEach((provider) => {
        const providerScore = attributeScores.scores.find(
          (score) => score.hrToolDirectusId === provider.hrToolDirectusId,
        );
        providerValuesByHrToolId.set(provider.hrToolDirectusId, providerScore?.score ?? null);
      });

      let importanceScore = null;
      let importanceLabelTranslationKey = null;
      if (customerAverageRating !== null) {
        importanceScore = this.getNearestImportanceScore(
          customerAverageRating,
          calculationResult.importanceScoreWeights,
        );
        if (importanceScore) {
          importanceLabelTranslationKey =
            this.attributeTransformationService.getImportanceScoreTranslationKey(importanceScore);
        }
      }

      rows.push({
        directusId: attr.id,
        scaNumber: attributeScores.scaNumber ?? attr.sca_number,
        label: this.directusTranslationPipe.transform(attr.translations, lang, attr.id) ?? '',
        fulfillmentType: attr.fulfillment_type
          ? (fulfillmentTypeById.get(attr.fulfillment_type) ?? null)
          : null,
        customerAverageRating,
        importanceScore,
        importanceLabelTranslationKey,
        providerValuesByHrToolId,
      });
    });
    return rows.sort((a, b) => a.scaNumber - b.scaNumber);
  });

  constructor() {
    effect(() => {
      if (!this.visible()) return;
      const providerIds = this.providerColumns().map((provider) => provider.hrToolDirectusId);
      this.selectedProviderIds.set(providerIds);
    });
  }

  protected onSelectedProvidersChange(selectedIds: string[]): void {
    this.selectedProviderIds.set(selectedIds);
  }

  private findSubcriteriaScores(
    criteria: ProviderScoreCalculationResult['requestedCriteria'],
    subCriteria: SubCriteriaRow,
  ) {
    for (const criterion of criteria) {
      const match = criterion.subCriteria.find(
        (sub) => sub.directusId === subCriteria.directusId || sub.scNumber === subCriteria.scNumber,
      );
      if (match) return match;
    }
    return null;
  }

  private mapScoresByAttribute(subCriteriaScores: {attributes: AttributeWithScores[]} | null) {
    const map = new Map<string, AttributeWithScores>();
    if (!subCriteriaScores) return map;
    subCriteriaScores.attributes.forEach((attribute) => {
      map.set(attribute.directusId, attribute);
    });
    return map;
  }

  private calculateCustomerAverage(
    scores: ImportanceScoreInfo[],
    weightMap: Map<string, number>,
  ): number | null {
    if (scores.length === 0) return null;
    const weights = scores
      .map((score) => weightMap.get(score.importanceScore))
      .filter((weight): weight is number => weight !== undefined);
    if (weights.length === 0) return null;
    const avg = weights.reduce((sum, weight) => sum + weight, 0) / weights.length;
    return Math.round(avg * 100) / 100;
  }

  private getNearestImportanceScore(
    score: number,
    weights: Map<string, number>,
  ): EvaluationSubCriteriaImportanceScore | null {
    let closestKey: string | null = null;
    let minDiff = Number.MAX_VALUE;

    for (const [key, value] of weights.entries()) {
      const diff = Math.abs(score - value);
      if (diff < minDiff) {
        minDiff = diff;
        closestKey = key;
      }
    }
    return closestKey as EvaluationSubCriteriaImportanceScore | null;
  }
}
